<template>
  <v-main>
    <v-container class="onboarding-container py-10">
      <h1 class="text-3xl font-bold">{{ t("onboarding.title") }}</h1>
      <p class="text-base text-medium-emphasis mt-2 mb-8">{{ t("onboarding.subtitle") }}</p>

      <v-card class="onboarding-card p-8" elevation="2" rounded="lg">
        <div class="mb-2 flex flex-wrap items-center justify-between gap-2">
          <div class="text-sm font-medium">{{ t("onboarding.countryLabel") }}</div>
          <v-btn
            class="text-medium-emphasis px-1 normal-case"
            variant="text"
            density="compact"
            prepend-icon="mdi-help-circle-outline"
            :href="`mailto:support@aircap.tech?subject=${encodeURIComponent(t('onboarding.countryMissingSubject'))}`"
          >
            {{ t("onboarding.countryMissingHelp") }}
          </v-btn>
        </div>
        <v-select
          v-model="country"
          autocomplete="organization-country"
          :items="countryItems"
          item-title="title"
          item-value="value"
          variant="outlined"
          hide-details
          class="mb-6"
          :disabled="creating"
        >
          <template #selection="{ item }">
            <span class="flex items-center gap-3">
              <span :class="item.flag"></span>
              {{ item.title }}
            </span>
          </template>
          <template #item="{ props: itemProps, item }">
            <v-list-item v-bind="itemProps">
              <template #prepend>
                <span class="mr-2" :class="item.flag"></span>
              </template>
            </v-list-item>
          </template>
        </v-select>

        <div class="mb-2 text-sm font-medium">
          {{ t("onboarding.organizationNameLabel") }}
        </div>
        <div ref="organizationField">
          <v-autocomplete
            v-model="selectedBusiness"
            v-model:menu="menuOpen"
            :search="search"
            :items="businesses"
            :disabled="creating"
            :hide-no-data="typedName.trim().length < 3"
            :hint="
              selectedBusiness?.siren
                ? t('onboarding.organizationMatchedHint')
                : t('onboarding.organizationNameHint')
            "
            :placeholder="t('onboarding.organizationNamePlaceholder')"
            :item-title="businessTitle"
            :item-value="(item: PappersSuggestion) => item.siren"
            persistent-hint
            prepend-inner-icon="mdi-magnify"
            variant="outlined"
            return-object
            no-filter
            clearable
            @update:search="onUpdateSearch"
            @click:clear="onClear"
          >
            <template #append-inner>
              <v-progress-circular
                v-if="searching"
                indeterminate
                color="primary"
                size="20"
                width="2"
              />
            </template>
            <template #item="{ props: itemProps, item }">
              <v-list-item v-bind="itemProps">
                <template #subtitle>
                  {{
                    [item.siege?.siret || item.siren, item.siege?.ville].filter(Boolean).join(" · ")
                  }}
                </template>
              </v-list-item>
            </template>
            <template #append-item>
              <template v-if="typedName.trim().length >= 3">
                <v-divider class="mt-1" />
                <v-list-item
                  :title="t('onboarding.organizationNotInList')"
                  prepend-icon="mdi-pencil-outline"
                  @mousedown.prevent
                  @click="keepTypedName"
                />
              </template>
            </template>
          </v-autocomplete>
        </div>

        <template
          v-if="
            selectedBusiness?.siren && !currentUserPersonKey && (loadingPeople || people.length)
          "
        >
          <div class="mt-6 mb-2 text-sm font-medium">{{ t("onboarding.personLabel") }}</div>
          <v-select
            v-model="selectedPersonKey"
            :items="personItems"
            :disabled="creating || loadingPeople"
            :loading="loadingPeople"
            :hint="t('onboarding.personHint')"
            :placeholder="t('onboarding.personPlaceholder')"
            item-title="title"
            item-value="key"
            persistent-hint
            variant="outlined"
          >
            <template #item="{ props: itemProps, item }">
              <v-divider v-if="item.key === 'not-in-list'" class="mt-1" />
              <v-list-item
                v-bind="itemProps"
                :prepend-icon="item.key === 'not-in-list' ? 'mdi-account-off-outline' : undefined"
              >
                <template v-if="item.subtitle" #subtitle>{{ item.subtitle }}</template>
              </v-list-item>
            </template>
          </v-select>
        </template>
      </v-card>

      <v-btn
        class="mt-8"
        color="primary"
        type="button"
        :loading="creating"
        :disabled="
          creating ||
          loadingPeople ||
          !organizationName ||
          (people.length > 0 && selectedPersonKey === null)
        "
        @mousedown.prevent
        @click="onNext"
      >
        {{ t("common.next") }}
      </v-btn>
    </v-container>
  </v-main>
</template>

<script lang="ts" setup>
import { useI18n } from "@/composables/useI18n";
import { useLanguage } from "@/composables/useLanguage";
import { call } from "@/firebase";
import { useToast } from "@/store";
import {
  PappersSuggestionsResponseSchema,
  pappersPersonKey,
  pappersPersonName,
  type PappersSuggestion,
} from "@aircap/common/pappers";
import type { TreezorCountryCode } from "@aircap/common/treezor";
import { captureException } from "@sentry/vue";
import { useDebounceFn } from "@vueuse/core";
import "flag-icons/css/flag-icons.min.css";
import { computed, ref, watch } from "vue";
import { useRouter } from "vue-router";

const { t, locale } = useI18n();
const { currentLanguage } = useLanguage();
const router = useRouter();
const { showErrorToast } = useToast();
const country = ref<TreezorCountryCode>("FR");
const search = ref("");
const typedName = ref("");
const selectedBusiness = ref<PappersSuggestion | null>(null);
const businesses = ref<PappersSuggestion[]>([]);
const people = ref<
  {
    key: string;
    firstname: string;
    lastname: string;
    qualites: string[];
    beneficiaireEffectif: boolean;
  }[]
>([]);
const selectedPersonKey = ref<string | null>(null);
const currentUserPersonKey = ref<string | null>(null);
const loadingPeople = ref(false);
const searching = ref(false);
const menuOpen = ref(false);
const organizationField = ref<HTMLElement | null>(null);
const creating = ref(false);
let searchRequestId = 0;
let peopleRequestId = 0;

const personItems = computed(() => [
  ...people.value.map((person) => ({
    key: person.key,
    title: [person.firstname, person.lastname].filter(Boolean).join(" "),
    subtitle: [...person.qualites, person.beneficiaireEffectif ? t("onboarding.personUbo") : ""]
      .filter(Boolean)
      .join(" · "),
  })),
  ...(people.value.length
    ? [{ key: "not-in-list", title: t("onboarding.personNotInList"), subtitle: "" }]
    : []),
]);

const businessTitle = (company: PappersSuggestion) =>
  (company.nom_entreprise || company.denomination || "").trim();

const organizationName = computed(() =>
  (selectedBusiness.value ? businessTitle(selectedBusiness.value) : typedName.value).trim(),
);

const fetchBusinesses = async (query: string) => {
  const trimmed = query.trim();
  if (trimmed.length < 3 || country.value !== "FR") {
    searchRequestId += 1;
    businesses.value = [];
    searching.value = false;
    return;
  }
  if (selectedBusiness.value && trimmed === businessTitle(selectedBusiness.value)) return;

  const requestId = ++searchRequestId;
  searching.value = true;
  try {
    const digits = trimmed.replaceAll(/\s/g, "");
    const url = new URL("https://suggestions.pappers.fr/v2");
    url.searchParams.set("q", /^\d{9}$/.test(digits) || /^\d{14}$/.test(digits) ? digits : trimmed);
    url.searchParams.set("cibles", "nom_entreprise,siren,siret");
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Pappers suggestions failed with status ${response.status}`);
    const parsed = PappersSuggestionsResponseSchema.parse(await response.json());
    if (requestId !== searchRequestId) return;
    const seen = new Set<string>();
    const results: PappersSuggestion[] = [];
    for (const company of [
      ...(parsed.resultats_siren ?? []),
      ...(parsed.resultats_siret ?? []),
      ...(parsed.resultats_nom_entreprise ?? []),
    ]) {
      if (seen.has(company.siren)) continue;
      seen.add(company.siren);
      if (company.entreprise_cessee === true || company.personne_morale === false) continue;
      results.push(company);
    }
    businesses.value = results;
  } catch (cause) {
    if (requestId !== searchRequestId) return;
    const error = new Error("Error searching companies", { cause });
    console.error(error);
    captureException(error);
    businesses.value = [];
  } finally {
    if (requestId === searchRequestId) searching.value = false;
  }
};

const onSearch = useDebounceFn((query: string) => {
  void fetchBusinesses(query);
}, 300);

const onUpdateSearch = (value: string) => {
  // VAutocomplete clears search on blur before focus events settle. That reset is not an edit.
  if (!organizationField.value?.contains(document.activeElement)) {
    if (!value && (typedName.value || selectedBusiness.value))
      search.value = selectedBusiness.value
        ? businessTitle(selectedBusiness.value)
        : typedName.value;
    return;
  }
  search.value = value;
  typedName.value = value;
  const query = value.trim();
  if (selectedBusiness.value && query && query !== businessTitle(selectedBusiness.value))
    selectedBusiness.value = null;
  if (query.length < 3) {
    onSearch.cancel();
    searchRequestId += 1;
    businesses.value = [];
    searching.value = false;
    menuOpen.value = false;
    return;
  }
  menuOpen.value = true;
  void onSearch(value);
};

const onClear = () => {
  onSearch.cancel();
  searchRequestId += 1;
  search.value = "";
  typedName.value = "";
  selectedBusiness.value = null;
  businesses.value = [];
  searching.value = false;
};

const keepTypedName = () => {
  const name = typedName.value.trim();
  if (!name) return;
  selectedBusiness.value = null;
  typedName.value = name;
  search.value = name;
  menuOpen.value = false;
};

watch(
  () => selectedBusiness.value?.siren,
  async (siren) => {
    selectedPersonKey.value = null;
    currentUserPersonKey.value = null;
    people.value = [];
    if (!siren) {
      peopleRequestId += 1;
      loadingPeople.value = false;
      return;
    }

    const requestId = ++peopleRequestId;
    loadingPeople.value = true;
    try {
      const response = await call("listPappersCompanyPeople")({ siren });
      if (requestId !== peopleRequestId) return;
      const deduped = new Map<string, (typeof people.value)[number]>();
      for (const row of response.people) {
        const key = pappersPersonKey(row);
        if (!key) continue;
        const qualite = row.qualite?.trim();
        const current = deduped.get(key);
        const { firstname, lastname } = pappersPersonName(row);
        if (!current) {
          deduped.set(key, {
            key,
            firstname,
            lastname,
            qualites: qualite ? [qualite] : [],
            beneficiaireEffectif: row.beneficiaireEffectif,
          });
          continue;
        }
        if (!current.firstname) current.firstname = firstname;
        if (!current.lastname) current.lastname = lastname;
        if (qualite && !current.qualites.includes(qualite)) current.qualites.push(qualite);
        if (row.beneficiaireEffectif) current.beneficiaireEffectif = true;
      }
      people.value = [...deduped.values()];
      currentUserPersonKey.value = response.currentUserPersonKey;
      if (response.currentUserPersonKey)
        selectedPersonKey.value = deduped.has(response.currentUserPersonKey)
          ? response.currentUserPersonKey
          : "not-in-list";
    } catch (cause) {
      if (requestId !== peopleRequestId) return;
      const error = new Error("Error loading company people", { cause });
      console.error(error);
      captureException(error);
      showErrorToast();
      people.value = [];
    } finally {
      if (requestId === peopleRequestId) loadingPeople.value = false;
    }
  },
);

const onNext = async () => {
  if (
    !organizationName.value ||
    creating.value ||
    loadingPeople.value ||
    (people.value.length > 0 && selectedPersonKey.value === null)
  )
    return;

  creating.value = true;
  try {
    const accountId = await call("createTreezorAccount")({
      accountName: organizationName.value,
      operatorLanguage: currentLanguage.value.code,
      siren: selectedBusiness.value?.siren,
      creatorPersonKey:
        selectedPersonKey.value && selectedPersonKey.value !== "not-in-list"
          ? selectedPersonKey.value
          : undefined,
    });
    await router.push({ name: "Account", params: { accountId } });
  } catch (cause) {
    const error = new Error("Failed to create project", { cause });
    console.error(error);
    captureException(error);
    showErrorToast(t("addAccountDialog.error"));
  } finally {
    creating.value = false;
  }
};

const countryItems = computed(() => {
  const displayNames = new Intl.DisplayNames([locale.value], { type: "region" });
  return [
    {
      title: displayNames.of("FR") ?? "FR",
      value: "FR" as const,
      flag: "fi fi-fr",
    },
  ];
});
</script>

<style scoped>
.onboarding-container {
  max-width: 920px;
}

.onboarding-card {
  max-width: 640px;
}
</style>
